import { File } from "@prisma/client";
import Dialog from "@reach/dialog";
import { IKContext, IKImage, IKUpload } from "imagekitio-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useFetcher, useMatches } from "remix";
import invariant from "tiny-invariant";
import { FilesAdminLoaderData } from "~/routes/admin/files";
import { NewFileActionData } from "~/routes/admin/files/new";
import { Button } from "./Button";
import { FormInput } from "./FormInput";
import { toast } from "./Toast";

export async function getUploadDialogConfig(
  request: Request
): Promise<UploadDialogConfig | null> {
  invariant(
    typeof process.env.IMAGEKIT_PUBLIC_KEY === "string",
    "IMAGEKIT_PUBLIC_KEY is required"
  );
  invariant(
    typeof process.env.IMAGEKIT_ID === "string",
    "IMAGEKIT_ID is required"
  );

  const url = new URL(request.url);
  return {
    publicKey: process.env.IMAGEKIT_PUBLIC_KEY,
    urlEndpoint: `https://ik.imagekit.io/${process.env.IMAGEKIT_ID}`,
    authenticationEndpoint: `//${url.host}/admin/imagekit-auth`,
  };
}

export interface UploadDialogConfig {
  publicKey: string;
  urlEndpoint: string;
  authenticationEndpoint: string;
}

export function useUploadConfig(): UploadDialogConfig | null {
  const matches = useMatches();

  return (
    matches.find((match) => match.data?.uploadConfig)?.data.uploadConfig ?? null
  );
}

export interface UploadDialogOwnProps {
  onFileCreated?: (file: File) => void;
  name: string;
  label: string;
  value?: string;
  fieldError?: string;
  file?: File | null;
}

type UploadFile = Omit<File, "id" | "createdAt" | "updatedAt">;

export function UploadDialog({
  onFileCreated,
  name,
  label,
  fieldError,
  value,
  file,
}: UploadDialogOwnProps) {
  const config = useUploadConfig();
  const [files, setFiles] = useState<Array<UploadFile>>([]);
  const [isOpen, setIsOpen] = useState(false);

  const handleSuccess = useCallback((file) => {
    // AITags: null
    // fileId: "620182e5e638eb42a5339894"
    // filePath: "/wut_gCi7zxhLr.jpg"
    // fileType: "image"
    // height: 499
    // name: "wut_gCi7zxhLr.jpg"
    // size: 43961
    // thumbnailUrl: "https://ik.imagekit.io/wdljk7lxz5j/tr:n-media_library_thumbnail/wut_gCi7zxhLr.jpg"
    // url: "https://ik.imagekit.io/wdljk7lxz5j/wut_gCi7zxhLr.jpg"
    // width: 1099

    setFiles((files) => [
      ...files,
      {
        alt: "",
        copyright: "",
        name: file.name,
        url: file.filePath,
      },
    ]);
  }, []);
  const handleError = useCallback((dunno) => {
    console.log(dunno);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setFiles([]);
  }, []);

  const fileFetcher = useFetcher<NewFileActionData>();
  const filesAdminFetcher = useFetcher<FilesAdminLoaderData>();

  useEffect(() => {
    if (filesAdminFetcher.type === "init") {
      filesAdminFetcher.load("/admin/files");
    }
  }, [filesAdminFetcher]);

  useEffect(() => {
    if (fileFetcher.type === "done" && fileFetcher.data.ok) {
      onFileCreated?.(fileFetcher.data.file);
      close();

      toast("File saved successfully");
    }
  }, [fileFetcher]);

  if (!config) return null;

  return (
    <IKContext
      publicKey={config.publicKey}
      urlEndpoint={config.urlEndpoint}
      authenticationEndpoint={config.authenticationEndpoint}
    >
      <label className="text-sm font-bold" data-forminput>
        <input type="hidden" name={name} value={value} />
        {file && (
          <IKImage
            className="flex-grow-0 flex-shrink-0 object-contain"
            key={file.url}
            path={file.url}
            width={150}
            transformation={[
              {
                width: 150,
              },
            ]}
          />
        )}
        <div className="my-3">
          <Button
            size="sm"
            variant="secondary"
            type="button"
            onClick={() => setIsOpen(true)}
          >
            {label}
          </Button>
        </div>
      </label>
      {fieldError ? (
        <p className="text-red-500" role="alert" id={`${name}-error`}>
          {fieldError}
        </p>
      ) : null}
      <Dialog
        isOpen={isOpen}
        aria-label="Upload a file"
        onDismiss={() => close()}
      >
        {files.map((file) => (
          <fileFetcher.Form
            method="post"
            action="/admin/files/new"
            className="flex flex-row align-top"
          >
            <IKImage
              className="flex-grow-0 flex-shrink-0 object-contain"
              key={file.url}
              path={file.url}
              width={200}
              transformation={[
                {
                  width: 200,
                },
              ]}
            />

            <div className="flex-grow ml-10">
              {!!fileFetcher.data?.formError && (
                <p className="text-red-500">{fileFetcher.data.formError}</p>
              )}
              <FormInput
                label="Alt Text"
                defaultValue={file.alt}
                name="alt"
                fieldError={fileFetcher.data?.fieldErrors?.alt?.[0]}
              />
              <FormInput
                label="Copyright"
                defaultValue={file.copyright}
                fieldError={fileFetcher.data?.fieldErrors?.copyright?.[0]}
                name="copyright"
              />
              <FormInput
                label="File name"
                defaultValue={file.name}
                fieldError={fileFetcher.data?.fieldErrors?.name?.[0]}
                name="name"
                readOnly
              />
              <FormInput
                label="File URL"
                defaultValue={file.url}
                name="url"
                readOnly
              />
              <Button type="submit" className="mt-10">
                Speichern
              </Button>
            </div>
          </fileFetcher.Form>
        ))}

        {files.length === 0 && (
          <Fragment>
            <h3 className="font-bold my-4">Upload new image:</h3>
            <IKUpload
              useUniqueFileName={true}
              isPrivateFile={false}
              onSuccess={handleSuccess}
              onError={handleError}
            />
            {!!onFileCreated && (
              <Fragment>
                <hr className="my-4" />
                <h3 className="font-bold my-4">Or: Pick existing image:</h3>
                <div className="grid grid-cols-[repeat(auto-fit,_minmax(140px,max-content))] gap-5 content-center items-center">
                  {filesAdminFetcher.data?.files.map((file) => (
                    <button
                      key={file.id}
                      onClick={() => {
                        close();
                        onFileCreated?.(file);
                      }}
                      className="border-2 rounded-md border-gray-100"
                    >
                      <IKImage
                        path={file.url}
                        className="w-full object-scale-down h-[140px]"
                        height={100}
                        transformation={[
                          {
                            width: 100,
                            height: 100,
                          },
                        ]}
                      />
                    </button>
                  ))}
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </Dialog>
    </IKContext>
  );
}
